import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../pages/AdminNews.css';

const sanitizeEditorHtml = (html) => {
  if (!html) return '';

  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/(href|src)\s*=\s*("|')\s*javascript:[^"']*("|')/gi, '$1="#"');
};

const hasVisibleText = (html) => {
  if (!html) return false;
  const plain = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return plain.length > 0;
};

const AdminNews = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category_id: '',
    img: '',
    is_published: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const editorRef = useRef(null);

  useEffect(() => {
    fetchNews();
    fetchCategories();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/news?includeDrafts=true', {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : {},
      });
      const data = await response.json();
      setNews(data);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news articles');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/categories');
      const data = await response.json();
      setCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.title.trim() || !hasVisibleText(formData.content) || !formData.category_id) {
      setError('Title, content, and category are required');
      return;
    }

    const token = localStorage.getItem('admin_token');

    try {
      const url = editingId ? `/api/news?id=${editingId}` : '/api/news';
      const method = editingId ? 'PUT' : 'POST';

      const payload = {
        ...formData,
        content: sanitizeEditorHtml(formData.content),
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to save news');
        return;
      }

      setSuccess(editingId ? 'News updated successfully' : 'News created successfully');
      setFormData({
        title: '',
        content: '',
        category_id: '',
        img: '',
        is_published: true,
      });
      setEditingId(null);
      setShowForm(false);
      fetchNews();
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Error:', err);
    }
  };

  const handleEdit = (article) => {
    const safeContent = sanitizeEditorHtml(article.content || '');
    setFormData({
      title: article.title,
      content: safeContent,
      category_id: article.category_id,
      img: article.img || '',
      is_published: article.is_published,
    });
    setEditingId(article.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this news article?')) {
      return;
    }

    const token = localStorage.getItem('admin_token');

    try {
      const response = await fetch(`/api/news?id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        setError('Failed to delete news');
        return;
      }

      setSuccess('News deleted successfully');
      fetchNews();
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error('Error:', err);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      title: '',
      content: '',
      category_id: '',
      img: '',
      is_published: true,
    });
    setEditingId(null);
  };

  const applyFormat = (command, value = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, value);
    const updatedHtml = sanitizeEditorHtml(editorRef.current.innerHTML);
    setFormData((prev) => ({ ...prev, content: updatedHtml }));
  };

  const handleEditorInput = (e) => {
    const updatedHtml = sanitizeEditorHtml(e.currentTarget.innerHTML);
    setFormData((prev) => ({ ...prev, content: updatedHtml }));
  };

  const insertLink = () => {
    const url = window.prompt('Enter link URL (https://...)');
    if (!url) return;
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        setError('Only http/https links are allowed.');
        return;
      }
      setError('');
      applyFormat('createLink', parsed.toString());
    } catch {
      setError('Please enter a valid URL.');
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  return (
    <div className="admin-page-container">
      <div className="admin-sidebar">
        <div className="admin-logo">
          <h2>Admin Panel</h2>
        </div>
        <nav className="admin-nav">
          <button onClick={() => navigate('/admin/dashboard')} className="nav-item">
            📊 Dashboard
          </button>
          <button onClick={() => navigate('/admin/categories')} className="nav-item">
            📁 Categories
          </button>
          <a href="#/" className="nav-item active">
            📰 News
          </a>
          <button
            onClick={() => {
              localStorage.removeItem('admin_token');
              localStorage.removeItem('admin_user');
              navigate('/admin/login');
            }}
            className="nav-item logout-btn"
          >
            🚪 Logout
          </button>
        </nav>
      </div>

      <div className="admin-main">
        <div className="admin-header">
          <h1>News Management</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="create-btn"
          >
            ➕ New Article
          </button>
        </div>

        <div className="admin-content">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {showForm && (
            <div className="form-container">
              <h2>{editingId ? 'Edit Article' : 'Create New Article'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="Enter article title"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) =>
                      setFormData({ ...formData, category_id: e.target.value })
                    }
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Content *</label>
                  <div className="rich-editor">
                    <div className="rich-editor-toolbar" role="toolbar" aria-label="Formatting toolbar">
                      <button type="button" className="rich-btn" onClick={() => applyFormat('bold')} title="Bold"><strong>B</strong></button>
                      <button type="button" className="rich-btn" onClick={() => applyFormat('italic')} title="Italic"><em>I</em></button>
                      <button type="button" className="rich-btn" onClick={() => applyFormat('underline')} title="Underline"><span style={{ textDecoration: 'underline' }}>U</span></button>
                      <button type="button" className="rich-btn" onClick={() => applyFormat('insertUnorderedList')} title="Bullet List">• List</button>
                      <button type="button" className="rich-btn" onClick={() => applyFormat('insertOrderedList')} title="Numbered List">1. List</button>
                      <button type="button" className="rich-btn" onClick={() => applyFormat('formatBlock', '<h3>')} title="Heading">H3</button>
                      <button type="button" className="rich-btn" onClick={() => applyFormat('formatBlock', '<p>')} title="Paragraph">P</button>
                      <button type="button" className="rich-btn" onClick={insertLink} title="Insert Link">Link</button>
                      <button type="button" className="rich-btn" onClick={() => applyFormat('removeFormat')} title="Clear Formatting">Clear</button>
                    </div>
                    <div
                      ref={editorRef}
                      className="rich-editor-input"
                      contentEditable
                      suppressContentEditableWarning
                      onInput={handleEditorInput}
                      dangerouslySetInnerHTML={{ __html: formData.content }}
                      aria-label="Article detail content editor"
                    />
                  </div>
                  <small className="field-help">Tip: use the toolbar to add bold, italic, headings, lists, and links.</small>
                </div>

                <div className="form-group">
                  <label>Image URL</label>
                  <input
                    type="url"
                    value={formData.img}
                    onChange={(e) =>
                      setFormData({ ...formData, img: e.target.value })
                    }
                    placeholder="https://example.com/news-image.jpg"
                  />
                </div>

                <div className="form-group checkbox">
                  <input
                    type="checkbox"
                    id="is_published"
                    checked={formData.is_published}
                    onChange={(e) =>
                      setFormData({ ...formData, is_published: e.target.checked })
                    }
                  />
                  <label htmlFor="is_published">Publish this article</label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingId ? 'Update Article' : 'Create Article'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="table-container">
            {loading ? (
              <p>Loading news articles...</p>
            ) : news.length === 0 ? (
              <p>No news articles found. Create one to get started!</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Views</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {news.map((article) => (
                    <tr key={article.id}>
                      <td>
                        <strong>{article.title}</strong>
                      </td>
                      <td>{getCategoryName(article.category_id)}</td>
                      <td>
                        <span
                          className={`status ${
                            article.is_published ? 'published' : 'draft'
                          }`}
                        >
                          {article.is_published ? '✓ Published' : '⊘ Draft'}
                        </span>
                      </td>
                      <td>{article.views}</td>
                      <td>{new Date(article.created_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          onClick={() => handleEdit(article)}
                          className="btn-small btn-edit"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDelete(article.id)}
                          className="btn-small btn-delete"
                        >
                          🗑️ Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNews;
